import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * Converts a 2-letter ISO country code to its flag emoji.
 * e.g. 'IN' → '🇮🇳', 'US' → '🇺🇸'
 */
function countryCodeToFlag(code) {
  if (!code || code.length !== 2) return '🌐';
  const base = 0x1F1E6 - 65; // Regional Indicator Symbol Letter A
  return String.fromCodePoint(
    base + code.toUpperCase().charCodeAt(0),
    base + code.toUpperCase().charCodeAt(1),
  );
}

// Pinned countries shown first — mirrors the currency priority order:
// USD→US, GBP→GB, EUR→EU (special), INR→IN
const PINNED_ORDER = { US: 1, GB: 2, IN: 3 };

/**
 * CountrySelect — same look & feel as CurrencySelect.
 *
 * Props:
 *   countries   — array of { code, name } from the /reference/countries API
 *   value       — selected country code (e.g. 'IN')
 *   onChange    — (code: string) => void
 *   error       — boolean, red border when true
 *   className   — extra class names for the trigger button
 */
export default function CountrySelect({
  countries = [],
  value = 'IN',
  onChange,
  error = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  const selected = countries.find((c) => c.code === value);
  const label = selected ? selected.name : value;

  // Sort: pinned countries first (US, GB, IN), then rest alphabetically
  const ordered = [...countries].sort((a, b) => {
    const oA = PINNED_ORDER[a.code] || 99;
    const oB = PINNED_ORDER[b.code] || 99;
    if (oA !== oB) return oA - oB;
    return a.name.localeCompare(b.name);
  });

  const filtered = ordered.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.code.toLowerCase().includes(q) ||
      c.name.toLowerCase().includes(q)
    );
  });

  const openDropdown = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropPos({
      top: rect.bottom + window.scrollY + 2,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
    setOpen(true);
    setSearch('');
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setSearch('');
  };

  const borderColor = error ? '#f87171' : open ? '#3b82f6' : '#cbd5e1';

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={open ? () => setOpen(false) : openDropdown}
        className={className}
        style={{
          width: '100%',
          height: '34px',
          border: `1px solid ${borderColor}`,
          borderRadius: '6px',
          background: '#fff',
          color: '#0f172a',
          fontSize: '13px',
          padding: '0 28px 0 10px',
          textAlign: 'left',
          cursor: 'pointer',
          outline: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
          position: 'relative',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
          {label}
        </span>
        <span
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
            transition: 'transform 0.15s ease',
            color: '#64748b',
            fontSize: '10px',
            pointerEvents: 'none',
            lineHeight: 1,
          }}
        >
          ▼
        </span>
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={listRef}
            style={{
              position: 'absolute',
              top: dropPos.top,
              left: dropPos.left,
              width: dropPos.width,
              zIndex: 99999,
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              overflow: 'hidden',
              maxHeight: '220px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                style={{
                  width: '100%',
                  height: '28px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  padding: '0 8px',
                  fontSize: '12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  color: '#0f172a',
                }}
              />
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filtered.length === 0 && (
                <div style={{ padding: '10px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                  No results
                </div>
              )}
              {filtered.map((c) => {
                const isSelected = c.code === value;
                return (
                  <div
                    key={c.code}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(c.code); }}
                    style={{
                      padding: '7px 10px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      background: isSelected ? '#eff6ff' : 'transparent',
                      color: isSelected ? '#1d4ed8' : '#0f172a',
                      fontWeight: isSelected ? 600 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      userSelect: 'none',
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ flex: 1 }}>{c.name}</span>
                    {isSelected && (
                      <span style={{ color: '#1d4ed8', fontSize: '11px' }}>✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
